/**
 * 阿里云流式TTS实现
 *
 * 协议：阿里云智能语音交互 WebSocket 协议（CosyVoice 大模型）
 * https://help.aliyun.com/zh/isi/developer-reference/websocket-protocol-description
 *
 * 流程：
 * StartSynthesis -> RunSynthesis(多次) -> StopSynthesis
 */

import BaseProvider from "../BaseProvider";
import {
    ALIYUN_CONFIG
} from "../../constants/AliyunConstants";

import {
    uuid
} from "../../../uuid";

const IDLE_STOP_DELAY = 6000;

export default class AliyunProvider extends BaseProvider {
    constructor(config = {}) {
        super(config);
        this.ws = null;
        this.taskId = null;
        this.started = false;
        this.closedByClient = false;
        this.pendingTexts = [];
        this.stopAfterStarted = false;
        this.stopping = false;
        this.idleTimer = null;
    }

    isStreaming() {
        return true;
    }

    speak(text) {
        const value = text?.trim();

        if (!value) {
            console.warn("[TTS] ignore empty text", JSON.stringify(text));
            return;
        }

        if (this.stopping) {
            this.close();
        }

        this.pendingTexts.push(value);
        this.ensureConnected();
        this.flushPendingTexts();
        this.resetIdleTimer();
    }

    finish() {
        this.clearIdleTimer();

        if (!this.ws) {
            this.onComplete?.();
            return;
        }

        if (this.stopping) {
            return;
        }

        if (!this.started) {
            this.stopAfterStarted = true;
            return;
        }

        this.flushPendingTexts();
        this.sendStop();
    }

    ensureConnected() {
        if (this.ws) {
            return;
        }

        const { token } = this.config;
        const url = `${ALIYUN_CONFIG.WS_URL}?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);

        this.ws = ws;
        this.taskId = uuid();
        this.started = false;
        this.closedByClient = false;
        this.stopAfterStarted = false;
        this.stopping = false;
        ws.binaryType = "arraybuffer";

        ws.onopen = () => {
            if (this.ws !== ws) {
                return;
            }

            console.log("[Aliyun TTS] websocket connected");
            this.sendStart();
        };

        ws.onmessage = event => {
            if (this.ws !== ws) {
                return;
            }

            this.handleMessage(event);
        };

        ws.onerror = event => {
            if (this.ws !== ws) {
                return;
            }

            console.error("[Aliyun TTS] error", event);
            this.clearSession();
            this.onError?.(event);
        };

        ws.onclose = event => {
            console.log("[Aliyun TTS] closed");

            if (this.ws !== ws) {
                return;
            }

            const shouldReportError =
                !this.closedByClient &&
                !event?.wasClean &&
                event?.code &&
                event.code !== 1000 &&
                event.code !== 1005;

            this.clearSession();

            if (shouldReportError) {
                this.onError?.(event);
            }
        };
    }

    sendStart() {
        const voice = this.config.voice || ALIYUN_CONFIG.VOICE;
        console.log('[AliyunProvider] StartSynthesis voice:', voice, 'config:', this.config);
        this.send({
            header: {
                message_id: uuid(),
                task_id: this.taskId,
                namespace: "FlowingSpeechSynthesizer",
                name: "StartSynthesis",
                appkey: this.config.appKey,
            },
            payload: {
                voice,
                model: this.config.model || ALIYUN_CONFIG.MODEL,
                format: this.config.format || ALIYUN_CONFIG.FORMAT,
                sample_rate: this.config.sampleRate || ALIYUN_CONFIG.SAMPLE_RATE,
                ...(this.config.volume !== undefined && { volume: this.config.volume }),
                ...(this.config.speechRate !== undefined && { speech_rate: this.config.speechRate }),
                ...(this.config.pitchRate !== undefined && { pitch_rate: this.config.pitchRate })
            }
        });
    }

    sendRun(text) {
        this.send({
            header: {
                message_id: uuid(),
                task_id: this.taskId,
                namespace: "FlowingSpeechSynthesizer",
                name: "RunSynthesis",
                appkey: this.config.appKey
            },
            payload: {
                text
            }
        });
    }

    sendStop() {
        this.stopping = true;
        this.send({
            header: {
                message_id: uuid(),
                task_id: this.taskId,
                namespace: "FlowingSpeechSynthesizer",
                name: "StopSynthesis",
                appkey: this.config.appKey
            }
        });
    }

    send(message) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            return;
        }

        console.log("[Aliyun TTS] send", message);
        this.ws.send(JSON.stringify(message));
    }

    handleMessage(event) {
        if (event.data instanceof ArrayBuffer) {
            this.onAudio?.(event.data);
            return;
        }

        const data = JSON.parse(event.data);
        console.log("[Aliyun TTS] message", data);

        if (data.header?.status && data.header.status !== 20000000) {
            this.close();
            this.onError?.(data);
            return;
        }

        switch (data.header?.name) {
            case "SynthesisStarted":
                // 服务端开始合成，此时可以批量发送待处理文本
                this.started = true;
                this.flushPendingTexts();
                if (this.stopAfterStarted) {
                    this.sendStop();
                }
                break;

            case "SentenceSynthesis":
            case "SentenceEnd":
                // CosyVoice 大模型不支持字幕时间戳，无需处理
                // 但保留事件处理以防服务端仍然下发
                break;

            case "SynthesisCompleted":
                // 服务端合成完毕，所有音频已下发完毕
                this.close();
                this.onComplete?.();
                break;

            default:
                break;
        }
    }

    flushPendingTexts() {
        if (!this.started || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            return;
        }

        while (this.pendingTexts.length) {
            this.sendRun(this.pendingTexts.shift());
        }
    }

    resetIdleTimer() {
        this.clearIdleTimer();
        this.idleTimer = window.setTimeout(() => {
            this.finish();
        }, this.config.idleStopDelay || IDLE_STOP_DELAY);
    }

    clearIdleTimer() {
        if (this.idleTimer) {
            window.clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
    }

    clearSession() {
        this.clearIdleTimer();
        this.ws = null;
        this.taskId = null;
        this.started = false;
        this.closedByClient = false;
        this.stopAfterStarted = false;
        this.stopping = false;
    }

    stop() {
        this.pendingTexts = [];
        this.close();
    }

    close() {
        this.clearIdleTimer();

        if (this.ws) {
            const ws = this.ws;
            this.closedByClient = true;
            this.ws = null;
            ws.close();
        }

        this.taskId = null;
        this.started = false;
        this.stopAfterStarted = false;
        this.stopping = false;
    }
}
